import { Col, Form, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Formik } from 'formik'
import PropTypes from 'prop-types'
import { useMemo, useCallback } from 'react'

import { useIsMobile } from '../../hooks'
import { formElementPropsGenerator } from '../../lib/forms'
import { Yup } from '../../lib/validations'
import { Input, PhoneNumber, SubmitButton } from '../Forms'
import PracticeSelect from '../Forms/PracticeSelect'
import RoleSelect from '../Forms/RoleSelect'
import Switch from '../Forms/Switch'
import { userShape } from '../prop-types'
import PositionSelect from '../Forms/PositionSelect'

import { USERS_PLACEHOLDERS, USERS_LABELS } from '../../lib/constants'

const INITIAL_VALUES = Object.keys(USERS_LABELS).reduce(
  (sum, key) => ({
    ...sum,
    [key]: '',
  }),
  {},
)
INITIAL_VALUES.active = true
INITIAL_VALUES.roleId = undefined
INITIAL_VALUES.position = undefined

const propsFor = formElementPropsGenerator({
  formName: 'user-form',
  labels: USERS_LABELS,
  placeholders: USERS_PLACEHOLDERS,
})

function needsPractice(roleValue) {
  return roleValue && roleValue.label && roleValue.label === 'practitioner'
}

function buildValidationSchema() {
  const validations = {
    firstName: Yup.string().min(2).required(),
    lastName: Yup.string().min(2).required(),
    email: Yup.string().email().required(),
    smsNumber: Yup.string().nullable().phone(),
    position: Yup.object(),
    practiceId: Yup.string().when('roleId', {
      is: value => needsPractice(value),
      then: schema => schema.nullable().required(),
      otherwise: schema => schema.nullable(),
    }),
    roleId: Yup.object().required(),
    nationalProviderId: Yup.string().when(['roleId', 'position'], {
      is: (roleId, position) =>
        needsPractice(roleId) && position?.value === 'provider',
      then: schema => schema.nullable().npi(),
      otherwise: schema => schema.nullable(),
    }),
  }
  return Yup.object().shape(validations)
}

// Keyed on `md`: below 768 the app is in its mobile layout, and the form goes
// vertical there rather than holding a label column beside every input.
const labelCol = {
  md: { span: 4, offset: 2 },
}

const wrapperCol = {
  md: { span: 12 },
}

const actionsWrapperCol = {
  md: { span: 12, offset: labelCol.md.span },
}

export default function UserForm({ userData = undefined, onSubmit }) {
  const isMobile = useIsMobile()
  const initialValues = userData
    ? {
        ...userData,
        roleId: { value: userData.role.id, label: userData.role.name },
        position: userData.position
          ? { value: userData.position, label: userData.positionLabel }
          : undefined,
        nationalProviderId:
          userData.role.name === 'practitioner' &&
          userData.position === 'provider'
            ? userData.nationalProviderId
            : undefined,
      }
    : INITIAL_VALUES
  const validationSchema = useMemo(
    () => buildValidationSchema(!userData),
    [userData],
  )

  const handleSubmit = useCallback(
    (values, formikHelpers) => {
      const isPractitioner = needsPractice(values.roleId)
      const cleanedValues = {
        ...values,
        position: isPractitioner ? values.position : null,
        practiceId: isPractitioner ? values.practiceId : null,
        nationalProviderId: isPractitioner ? values.nationalProviderId : null,
      }

      return onSubmit(cleanedValues, formikHelpers)
    },
    [onSubmit],
  )

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({
          handleSubmit: submitForm,
          isSubmitting,
          isValid,
          values,
          submitCount,
        }) => (
          <Form
            layout={isMobile ? 'vertical' : 'horizontal'}
            onFinish={submitForm}
            labelCol={isMobile ? undefined : labelCol}
            wrapperCol={isMobile ? undefined : wrapperCol}
          >
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.firstName}>
              <Input {...propsFor('firstName')} label="" />
            </Form.Item>
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.lastName}>
              <Input {...propsFor('lastName')} label="" />
            </Form.Item>
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.email}>
              <Input {...propsFor('email')} type="email" label="" />
            </Form.Item>
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.smsNumber}>
              <PhoneNumber {...propsFor('smsNumber')} label="" />
            </Form.Item>
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.active}>
              <Switch {...propsFor('active')} label="" />
            </Form.Item>
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.password}>
              An invitation email will be sent to the user from which they will
              be able to set their own password
            </Form.Item>
            <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.roleId}>
              <RoleSelect {...propsFor('roleId')} label="" />
            </Form.Item>
            {needsPractice(values.roleId) && (
              <>
                <Form.Item labelCol={{ md: { span: 5 } }} label={USERS_LABELS.position}>
                  <PositionSelect {...propsFor('position')} label="" />
                </Form.Item>
                <Form.Item
                  labelCol={{ md: { span: 5 } }}
                  label={USERS_LABELS.practiceId}
                >
                  <PracticeSelect
                    {...propsFor('practiceId', { hasCustomEvents: true })}
                    clearWhenUnmount
                    label=""
                  />
                </Form.Item>
                {values.position?.value === 'provider' && (
                  <Form.Item
                    labelCol={{ md: { span: 5 } }}
                    label={
                      <span>
                        NPI{' '}
                        <Tooltip title={USERS_LABELS.nationalProviderId}>
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                  >
                    <Input {...propsFor('nationalProviderId')} label="" />
                  </Form.Item>
                )}
              </>
            )}
            <Col xs={24} md={{ span: 12, offset: 8 }}>
              <SubmitButton
                isSubmitting={isSubmitting}
                isValid={isValid}
                submitCount={submitCount}
                title={userData ? 'Save User' : 'Create User'}
                wrapperCol={actionsWrapperCol}
              />
            </Col>
          </Form>
        )}
      </Formik>
      <style>
        {`
        .ant-form-item {
          margin-bottom: 12px;
        }
        `}
      </style>
    </div>
  )
}

UserForm.propTypes = {
  userData: userShape,
  onSubmit: PropTypes.func.isRequired,
}
